import {
  Assessment,
  Description,
  LocationOn,
  MonetizationOn,
  People,
  ReceiptLong,
  ShoppingCart,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { useApiUrl, useCustom, useList } from "@refinedev/core";
import { RefineListView } from "../../components/refine-list-view";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";

type DashboardCard = {
  title: string;
  value: string | number;
  loading: boolean;
  icon: React.ReactElement;
  color: string;
};

export const DashboardPage = () => {
  const apiUrl = useApiUrl();

  const stockTakingReportCount = useCustom({
    url: `${apiUrl}/AdminStockTakingReports/$count`,
    method: "get",
  });
  
  const rfqCount = useCustom({
    url: `${apiUrl}/AdminRfqs/$count`,
    method: "get",
  });
  
  const userCount = useCustom({
    url: `${apiUrl}/AdminUsers/$count`,
    method: "get",
  });
  
  const portCount = useCustom({
    url: `${apiUrl}/AdminPorts/$count`,
    method: "get",
  });
  
  const manningReportCount = useCustom({
    url: `${apiUrl}/AdminManningReports/$count`,
    method: "get",
  });
  
  const cashPoCount = useCustom({
    url: `${apiUrl}/AdminCashPurchaseOrder/$count`,
    method: "get",
  });
  
  const adminOrdersCount = useCustom({
    url: `${apiUrl}/AdminOrders/$count`,
    method: "get",
  });

  // Use useList to fetch data for charts
  const ordersListData = useList({
    resource: "AdminOrders",
    pagination: { pageSize: 100 },
  });

  const usersListData = useList({
    resource: "AdminUsers",
    pagination: { pageSize: 100 },
  });

  const portsListData = useList({
    resource: "AdminPorts",
    pagination: { pageSize: 100 },
  });

  // Debug logging to show actual data structure
  console.log('Dashboard API Responses:', {
    stockTakingReportCount,
    rfqCount,
    userCount,
    portCount,
    manningReportCount,
    cashPoCount,
    adminOrdersCount
  });

  // Helper function to safely extract data from useCustom response
  const extractData = (response: any): { data: any; loading: boolean } => {
    if (!response) return { data: null, loading: true };
    
    // Handle different possible response structures
    const data = response.data || response.response?.data || response;
    const loading = response.isLoading || response.loading || false;
    
    return { data, loading };
  };

  const stockData = extractData(stockTakingReportCount);
  const rfqData = extractData(rfqCount);
  const userData = extractData(userCount);
  const portData = extractData(portCount);
  const manningData = extractData(manningReportCount);
  const cashData = extractData(cashPoCount);
  const ordersData = extractData(adminOrdersCount);

  // Helper function to format the count value properly
  const formatCount = (response: any): string => {
    if (!response) return "0";
    
    // Handle the useCustom response structure
    // The actual data is nested in query.data.data or result.data
    let data = null;
    
    // Try to extract from the nested structure
    if (response.query?.data?.data !== undefined) {
      data = response.query.data.data;
    } else if (response.result?.data !== undefined) {
      data = response.result.data;
    } else if (response.data?.data !== undefined) {
      data = response.data.data;
    } else if (response.data !== undefined) {
      data = response.data;
    } else {
      data = response;
    }
    
    // If it's already a number, return as string
    if (typeof data === 'number') return data.toString();
    
    // If it's a string that looks like a number, return it
    if (typeof data === 'string' && !isNaN(Number(data))) return data;
    
    // If it's an object, try to extract the count value
    if (typeof data === 'object') {
      // Try common property names for count values
      const possibleKeys = ['count', 'value', 'total', 'data'];
      for (const key of possibleKeys) {
        if (data[key] !== undefined) {
          const value = data[key];
          if (typeof value === 'number') return value.toString();
          if (typeof value === 'string' && !isNaN(Number(value))) return value;
        }
      }
      
      // If it's an array, return the length
      if (Array.isArray(data)) return data.length.toString();
      
      // Otherwise, JSON stringify to see the structure
      console.log('Object data structure:', data);
      return JSON.stringify(data);
    }
    
    return String(data);
  };

  const cards: DashboardCard[] = [
    {
      title: "Stock Taking Reports",
      value: formatCount(stockData.data),
      loading: stockData.loading,
      icon: <Assessment fontSize="large" />,
      color: "#1976d2",
    },
    {
      title: "RFQs",
      value: formatCount(rfqData.data),
      loading: rfqData.loading,
      icon: <ReceiptLong fontSize="large" />,
      color: "#388e3c",
    },
    {
      title: "Users",
      value: formatCount(userData.data),
      loading: userData.loading,
      icon: <People fontSize="large" />,
      color: "#f57c00",
    },
    {
      title: "Ports",
      value: formatCount(portData.data),
      loading: portData.loading,
      icon: <LocationOn fontSize="large" />,
      color: "#7b1fa2",
    },
    {
      title: "Manning Reports",
      value: formatCount(manningData.data),
      loading: manningData.loading,
      icon: <Description fontSize="large" />,
      color: "#c2185b",
    },
    {
      title: "Cash Purchase Orders",
      value: formatCount(cashData.data),
      loading: cashData.loading,
      icon: <MonetizationOn fontSize="large" />,
      color: "#0097a7",
    },
  ];

  // Prepare chart data
  const chartData = [
    { name: "Users", value: parseInt(formatCount(userData.data)) || 0 },
    { name: "Ports", value: parseInt(formatCount(portData.data)) || 0 },
    { name: "RFQs", value: parseInt(formatCount(rfqData.data)) || 0 },
  ];

  const pieData = [
    { name: "Stock Reports", value: parseInt(formatCount(stockData.data)) || 0, color: "#8884d8" },
    { name: "Manning Reports", value: parseInt(formatCount(manningData.data)) || 0, color: "#82ca9d" },
    { name: "Cash POs", value: parseInt(formatCount(cashData.data)) || 0, color: "#ffc658" },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  return (
    <RefineListView>
      {/* Summary Cards */}
      <Grid container spacing={4} justifyContent="center" sx={{ mb: 4 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} lg={4} key={index}>
            <Card
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                boxShadow: 3,
                borderLeft: `6px solid ${card.color}`,
              }}
            >
              <Box
                sx={{
                  mr: 2,
                  color: card.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {card.icon}
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  {card.title}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  {card.loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    card.value ?? "-"
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Entity Count Overview
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
        
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Reports Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>
    </RefineListView>
  );
};
